import { renderChallengePage } from './pages/challenge';
import { renderCleaningPage, seedCleaningIfEmpty } from './pages/cleaning';
import { renderCleaningEditPage } from './pages/cleaningEdit';
import { renderHabitsPage } from './pages/habits';
import { renderHistoryPage, resetHistoryScreenState } from './pages/history';
import { renderHomePage, seedHabitsIfEmpty } from './pages/home';
import { renderProfilePage } from './pages/profile';
import { renderRewardsPage } from './pages/rewards';
import { renderStreakPage } from './pages/streak';
import { renderTodoPage, resetTodoScreenState } from './pages/todo';
import { renderComingSoon } from './ui/comingSoon';
import { createTabBar, updateTabBarActiveState, type RouteId } from './ui/tabBar';

/**
 * Uygulama kabuğu ve gezinme.
 *
 * Bir router kütüphanesi kullanmıyoruz: uygulamanın 7 ekranı var ve
 * hepsi tek bir "şu an hangi ekrandayız" değişkeniyle yönetilebiliyor.
 * URL'yi de değiştirmiyoruz — ana ekrana eklenmiş bir PWA'da adres
 * çubuğu görünmez, dolayısıyla URL'nin kullanıcıya bir faydası olmaz;
 * buna karşılık iOS'ta geri/ileri hareketleriyle uğraşmak zorunda
 * kalırdık.
 */

let contentElement: HTMLElement | null = null;
let tabBarElement: HTMLElement | null = null;
let currentRoute: RouteId = 'home';

/**
 * Seçilen ekranı içerik alanına çizer.
 *
 * Her ekran kendi HTML'ini sıfırdan kurar; "hangi parça değişti"
 * hesabı yapmıyoruz. Ekranlar küçük olduğu için bu yaklaşım hem daha
 * basit hem de gözle fark edilir bir yavaşlığa yol açmıyor.
 */
async function renderRoute(container: HTMLElement, route: RouteId): Promise<void> {
  switch (route) {
    case 'home':
      await renderHomePage(container, navigate);
      return;
    case 'streak':
      await renderStreakPage(container, navigate);
      return;
    case 'cleaning':
      await renderCleaningPage(container, navigate);
      return;
    case 'cleaningEdit':
      await renderCleaningEditPage(container, navigate);
      return;
    case 'challenge':
      await renderChallengePage(container);
      return;
    // Spor henüz planlanmadı; yer tutucuyu kullanıyor.
    case 'sport':
      renderComingSoon(container, { title: 'Spor', icon: 'dumbbell' });
      return;
    case 'todo':
      await renderTodoPage(container);
      return;
    case 'profile':
      await renderProfilePage(container, navigate);
      return;
    case 'habitEdit':
      await renderHabitsPage(container, navigate);
      return;
    case 'rewards':
      await renderRewardsPage(container, navigate);
      return;
    case 'history':
      await renderHistoryPage(container, navigate);
      return;
  }
}

/**
 * Başka bir ekrana geçer.
 *
 * Geçişte içerik alanı en üste kaydırılır: aksi halde uzun bir
 * listeden başka bir ekrana geçince yeni ekran ortasından açılmış
 * gibi görünürdü.
 */
export async function navigate(route: RouteId): Promise<void> {
  if (!contentElement || !tabBarElement) return;

  // Yapılacaklar ekranı "hangi maddeyi düzenliyoruz" bilgisini kendi
  // modülünde tutuyor. Sekmeden yeniden girildiğinde bu sıfırlanmalı:
  // aksi halde kullanıcı düzenleme formundayken başka bir sekmeye
  // geçip geri döndüğünde ekran listeyi değil o formu açardı.
  if (route === 'todo' && currentRoute !== 'todo') {
    resetTodoScreenState();
  }
  // Aynı gerekçe Detaylı Geçmiş için de geçerli: seçili üç aylık dönem
  // ekrandan çıkınca sıfırlanmalı, yoksa ekran eski bir çeyrekte açılır.
  if (route === 'history' && currentRoute !== 'history') {
    resetHistoryScreenState();
  }

  currentRoute = route;
  updateTabBarActiveState(tabBarElement, route);
  await renderRoute(contentElement, route);
  contentElement.scrollTop = 0;
}

/** Mevcut ekranı verinin son hâliyle yeniden çizer. */
export async function refreshCurrentRoute(): Promise<void> {
  if (!contentElement) return;
  await renderRoute(contentElement, currentRoute);
}

/**
 * Uygulama kabuğunu kurar: kaydırılabilir içerik alanı + sabit alt menü.
 * Uygulama ömründe bir kez çağrılır.
 */
export async function startApp(root: HTMLElement): Promise<void> {
  // Başlangıç verisi TEK yerden ekleniyor. Ekranlar kendi verisini
  // kendisi tohumlasaydı, Anasayfa "Temizlik" alışkanlığının durumunu
  // hesaplarken temizlik görevleri henüz yaratılmamış olabilirdi —
  // görev olmayınca §7.5 kuralı "yapıldı" derdi ve kullanıcı hak
  // etmediği bir puan alırdı.
  await Promise.all([seedHabitsIfEmpty(), seedCleaningIfEmpty()]);

  root.innerHTML = '';
  root.className = 'app-shell';

  contentElement = document.createElement('main');
  contentElement.className = 'app-content';

  tabBarElement = createTabBar(currentRoute, (route) => {
    void navigate(route);
  });

  root.appendChild(contentElement);
  root.appendChild(tabBarElement);

  await renderRoute(contentElement, currentRoute);
}
